/** CrmEvidenceTimeline — the raw evidence timeline for a deal record (proposed candidate OR committed
 * opp): the emails/meetings the classifier bound to it (deal_event_log), newest first. Data comes from
 * the CRM-governed /evidence endpoint (useCrmRecordEvidence), which D8-gates the row + re-applies the
 * per-mailbox §9 gate server-side — so a row that renders here is already one the viewer is entitled to.
 *
 * ⚑ REFUTE (2026-08-10): each row carries the same affordance the Deals tab has, routed to the
 * CRM-native endpoint rather than `/api/deal-cards/…/unbind-event/…`, which applies neither CRM gate.
 * The dialog itself is shared with the consolidated record timeline — `CrmRefuteEvidenceModal`, which
 * carries the reason vocabulary, the error unwrap, and the ⛔ on where it may be mounted.
 *
 * E1b-0 (2026-08-21): the ⚑ renders from the server's `can_refute` for THIS row, no longer on every
 * row. It used to be offered to everyone and refused by the route — a dead button for anyone outside
 * the deal's audience who is not the event's inbox owner, which is mostly MEETING rows (they have no
 * inbox owner at all, and `_load_evidence` deliberately shows them to everyone). Nobody loses an
 * action: affordance-follows-the-server is the rule the record timeline's pending queue already obeys.
 *
 * Pillar F.5 (owner ruling 2026-08-20): rows carry NO body text. `body_available` decides whether the
 * row offers an expander; the body itself is fetched per row on the click, from the audited
 * `…/evidence/{event_log_id}/body` door. Opening a record no longer egresses every readable body to
 * the browser — nor reads them server-side — and each body read is now attributable to the moment
 * someone asked for it.
 *
 * WP-6b (2026-09-05): `anchor` is an optional `event_log_id` the caller wants brought into view —
 * the "why?" popover passes the citation the operator clicked. It SCROLLS and MARKS, it never
 * filters: the row's neighbours are the context that makes a citation legible, and hiding them
 * would turn "show me this email" into "show me only this email", which is lever F's job and has
 * its own gate. Honours `prefers-reduced-motion`.
 *
 * Interleaving notes / the subject timeline are still deferred follow-ons. */
import { useEffect, useRef, useState } from 'react';

import { useCrmRecordEvidence, useCrmRecordEvidenceBody } from '../../hooks/useCrm';
import type { EvidenceItem } from '../../types';
import { EvidenceBody, evidenceBodyState } from '../shared/EvidenceBody';
import { CrmRefuteEvidenceModal } from './CrmRefuteEvidenceModal';
import { fmtRowWhen } from './crmFormat';
import styles from './crm.module.css';

/** ⛔ SHARED FORMATTER, NOT A PRIVATE ONE (timeline-row-layout-spec §3). This was
 *  `new Date(iso).toLocaleString(undefined, …)`: it parsed the wire's naive UTC as browser-local and
 *  rendered in the browser's zone, while the popover that EMBEDS this stream
 *  (`CrmFactEvidencePopover`) prints its own dates through `fmtShortDate`, which is now ET. Same
 *  popover, two zones — an email at 02:00 UTC read "Sep 18" above and "Sep 19" below. */
function fmtWhen(iso: string | null): string {
  const { date, time } = fmtRowWhen(iso);
  return time ? `${date} · ${time}` : date;
}

function fmtTo(to: string[]): string {
  if (!to || to.length === 0) return '';
  if (to.length <= 2) return to.join(', ');
  return `${to.slice(0, 2).join(', ')} +${to.length - 2}`;
}

/** F.5 — ONE evidence row's body, fetched because this component MOUNTED, and it mounts only when the
 * operator expands the row. The hook lives here rather than on the row so "no request until a click"
 * is a structural property of the tree, not a rule the next editor has to remember. */
function EvidenceRowBody({ listId, entryId, eventLogId }: {
  listId: number; entryId: number; eventLogId: number;
}) {
  const q = useCrmRecordEvidenceBody(listId, entryId, eventLogId, true);
  return <EvidenceBody className={styles.evBody} state={evidenceBodyState(q)} body={q.data?.body} />;
}

function EvidenceTimelineRow({ ev, listId, entryId, onRefute, anchored }: {
  ev: EvidenceItem; listId: number; entryId: number; onRefute: () => void;
  /** WP-6b — this is the row the caller asked to be brought into view. */
  anchored?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  // Scroll on MOUNT and on a change of `anchored`, never on every render: the rows re-render on an
  // unrelated body expand, and re-scrolling then would yank the page out from under a reader.
  useEffect(() => {
    if (!anchored || !rowRef.current) return;
    const still = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Optional-called: jsdom does not implement `scrollIntoView`, and a provenance surface must not
    // throw on the environment its own tests run in. The mark below is what carries the meaning; the
    // scroll is the convenience.
    rowRef.current.scrollIntoView?.({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  }, [anchored]);
  // F.5: `body_available` is the server's "there is a body here AND you may read it" — the same gate
  // that used to decide whether `body_preview` rode inline. Same affordance, same population; only
  // the moment of the read moved.
  const canExpand = ev.body_available;
  const to = fmtTo(ev.recipients_to);
  return (
    // `aria-current` rather than a colour alone: the mark has to reach a screen reader, and "this is
    // the one you asked for" is exactly what `aria-current="true"` says.
    <li ref={rowRef} className={styles.evRow} data-anchored={anchored ? ev.event_log_id : undefined}
        aria-current={anchored ? true : undefined}>
      <div className={styles.evRowHead}
        onClick={canExpand ? () => setExpanded((e) => !e) : undefined}
        style={{ cursor: canExpand ? 'pointer' : 'default' }}
        data-evidence-toggle={canExpand ? ev.event_log_id : undefined}
        aria-expanded={canExpand ? expanded : undefined}>
        <span className={styles.evChevron}>{canExpand ? (expanded ? '▾' : '▸') : ''}</span>
        <span className={styles.evSubject} title={ev.subject ?? ''}>{ev.subject || '(no subject)'}</span>
        {ev.inbox_count > 1 && <span className={styles.evInboxBadge}>+{ev.inbox_count} inboxes</span>}
        <span className={styles.evWhen}>{fmtWhen(ev.occurred_at)}</span>
        {/* E1b-0 — the server's answer for THIS row and THIS caller (`may_refute_event`), never a
            client guess. The click is handed UP: the dialog is owned by the list, because a successful
            refute removes this row from the payload and would take the confirmation down with it. */}
        {ev.can_refute && (
          <button type="button" className={styles.evRefuteBtn} title="Refute evidence"
            aria-label="Refute evidence"
            onClick={(e) => { e.stopPropagation(); onRefute(); }}>⚑</button>
        )}
      </div>
      <div className={styles.evParties}>
        <span className={styles.evFrom}>{ev.sender || '—'}</span>
        {to && <span className={styles.evTo}>→ {to}</span>}
      </div>
      {expanded && canExpand && (
        <EvidenceRowBody listId={listId} entryId={entryId} eventLogId={ev.event_log_id} />
      )}
    </li>
  );
}

export function CrmEvidenceTimeline(
  { listId, entryId, anchor }:
  { listId: number; entryId: number;
    /** WP-6b — an `event_log_id` to bring into view and mark. Absent ⇒ unchanged behaviour. */
    anchor?: number | null },
) {
  const q = useCrmRecordEvidence(listId, entryId);
  // ⛔ The refute target lives HERE, not on the row — see `CrmRefuteEvidenceModal`'s header. The
  // SUBJECT is carried in the state rather than re-read from the row on render, and that is the point:
  // on success the row is gone from the payload, and the dialog still has to name the email it removed.
  const [refuteFor, setRefuteFor] = useState<{ eventLogId: number; subject: string | null } | null>(null);
  if (q.isLoading) return <div className={styles.evEmpty}>Loading evidence…</div>;
  if (q.isError) return <div className={styles.evEmpty}>Couldn’t load evidence.</div>;
  const rows = q.data?.evidence ?? [];
  const hidden = q.data?.hidden_count ?? 0;
  // The empty state may NOT short-circuit while a refute dialog is open: refuting the last row empties
  // the list, and returning early here would unmount the confirmation — the very thing hoisting the
  // dialog out of the row was for.
  if (rows.length === 0 && hidden === 0 && refuteFor === null) {
    return <div className={styles.evEmpty}>No evidence is bound to this deal yet.</div>;
  }
  return (
    <div className={styles.evTimeline}>
      <ul className={styles.evList}>
        {rows.map((ev) => (
          <EvidenceTimelineRow key={ev.event_log_id} ev={ev} listId={listId} entryId={entryId}
            anchored={anchor != null && ev.event_log_id === anchor}
            onRefute={() => setRefuteFor({ eventLogId: ev.event_log_id, subject: ev.subject })} />
        ))}
      </ul>
      {rows.length === 0 && hidden === 0 && (
        <div className={styles.evEmpty}>No evidence is bound to this deal yet.</div>
      )}
      {hidden > 0 && (
        <div className={styles.evHidden}>
          +{hidden} more email{hidden === 1 ? '' : 's'} not scanned from your mailbox
        </div>
      )}
      {refuteFor && (
        <CrmRefuteEvidenceModal listId={listId} entryId={entryId} eventLogId={refuteFor.eventLogId}
          subject={refuteFor.subject} onClose={() => setRefuteFor(null)} />
      )}
    </div>
  );
}
